import { useMemo, useState } from 'react';
import { useResource } from '../hooks/useResource';
import { api } from '../lib/api';
import type { CannedResponse } from '../lib/types';

const SHORTCUT_RE = /^\/[a-z0-9_-]+$/i;

export function SaveToCannedModal({
  content,
  onClose,
}: {
  content: string;
  onClose: (msg?: string, ok?: boolean) => void;
}) {
  const [shortcut, setShortcut] = useState('');
  const [title, setTitle] = useState('');
  const [folder, setFolder] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Existing folders off the live canned list (the same snapshot the composer
  // picker reads). A datalist, not a select: picking an existing folder is one
  // click and inventing a new one is one keystroke.
  const { items: canned } = useResource<CannedResponse>('canned_responses');
  const folders = useMemo(
    () =>
      [...new Set(canned.map((c) => c.folder?.trim()).filter((f): f is string => !!f))].sort((a, b) =>
        a.localeCompare(b),
      ),
    [canned],
  );

  // Auto-prefix "/" so the agent can type the bare word.
  const normalized = shortcut.trim() ? (shortcut.trim().startsWith('/') ? shortcut.trim() : `/${shortcut.trim()}`) : '';
  const valid = SHORTCUT_RE.test(normalized);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!valid || busy) return;
    setBusy(true);
    setErr(null);
    try {
      await api.create('canned', {
        shortcut: normalized,
        content,
        ...(title.trim() ? { title: title.trim() } : {}),
        ...(folder.trim() ? { folder: folder.trim() } : {}),
      });
      onClose('Saved to canned responses', true);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => onClose()}>
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-4" onClick={(e) => e.stopPropagation()}>
        <div className="text-sm font-semibold mb-3">Save as canned response</div>
        <form onSubmit={submit} className="space-y-2.5">
          <div>
            <label className="block text-xs text-gray-600 mb-0.5">Shortcut</label>
            <input
              type="text"
              value={shortcut}
              onChange={(e) => setShortcut(e.target.value)}
              placeholder="/greeting"
              autoFocus
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm font-mono"
              required
            />
            {shortcut.trim() && !valid && (
              <div className="text-[11px] text-red-600 mt-0.5">
                Use letters, numbers, “-” or “_” (e.g. /greeting).
              </div>
            )}
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-0.5">Title (optional)</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={120}
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-0.5">Folder (optional)</label>
            <input
              type="text"
              value={folder}
              onChange={(e) => setFolder(e.target.value)}
              list="canned-folders"
              maxLength={60}
              placeholder="Uncategorized"
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
            />
            <datalist id="canned-folders">
              {folders.map((f) => (
                <option key={f} value={f} />
              ))}
            </datalist>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-0.5">Content</label>
            <textarea
              value={content}
              readOnly
              rows={5}
              dir="auto"
              className="w-full border border-gray-200 bg-gray-50 rounded px-2 py-1 text-sm text-gray-700"
            />
          </div>
          {err && <div className="text-xs text-red-600">{err}</div>}
          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={() => onClose()}
              className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy || !valid}
              className="px-3 py-1 text-sm bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50"
            >
              {busy ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
